import { useEffect, useState } from 'react';
import { X, Users, LinkIcon } from 'lucide-react';
import type { MesaVista } from '@shared/types';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

interface Props {
  abierto: boolean;
  pedidoId: number | null;
  mesaPrincipalId: number | null;
  mesasYaUnidas: number[];
  onClose: () => void;
  onUnido: () => void;
}

/**
 * Modal multi-selector para unir N mesas a un pedido en curso (Sprint 6 RN-S6-01).
 * Excluye la mesa principal y las ya unidas. Solo lista mesas en estado `disponible`.
 */
export function JuntarMesasModal({
  abierto,
  pedidoId,
  mesaPrincipalId,
  mesasYaUnidas,
  onClose,
  onUnido,
}: Props) {
  const [mesas, setMesas] = useState<MesaVista[]>([]);
  const [seleccionadas, setSeleccionadas] = useState<Set<number>>(new Set());
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sesion = useSessionStore((s) => s.sesion);

  useEffect(() => {
    if (!abierto) {
      setMesas([]);
      setSeleccionadas(new Set());
      setError(null);
      return;
    }
    setCargando(true);
    window.api.mesas.listar().then((res) => {
      if (res.ok) setMesas(res.data);
      setCargando(false);
    });
  }, [abierto]);

  if (!abierto) return null;

  const excluidas = new Set<number>([
    ...(mesaPrincipalId !== null ? [mesaPrincipalId] : []),
    ...mesasYaUnidas,
  ]);
  const disponibles = mesas.filter(
    (m) => m.estado === 'disponible' && !excluidas.has(m.id),
  );

  const toggle = (id: number) => {
    setSeleccionadas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleUnir = async () => {
    if (!pedidoId || seleccionadas.size === 0 || !sesion) return;
    setEnviando(true);
    setError(null);
    const res = await window.api.pedidos.juntarMesas({
      pedidoId,
      mesaIds: Array.from(seleccionadas),
      usuarioId: sesion.usuarioId,
    });
    setEnviando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }
    onUnido();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <LinkIcon className="h-5 w-5 text-primary" />
            Juntar mesas
          </h2>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar" disabled={enviando}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 space-y-3">
          <p className="text-sm text-slate-500">
            Selecciona las mesas adicionales que quedarán unidas a este pedido.
            Solo se muestran mesas disponibles.
          </p>

          {cargando ? (
            <div className="text-center text-slate-400 py-8">Cargando mesas...</div>
          ) : disponibles.length === 0 ? (
            <div className="text-center text-slate-400 py-8 text-sm">
              No hay mesas disponibles para unir.
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {disponibles.map((m) => {
                const sel = seleccionadas.has(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggle(m.id)}
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-3 transition ${
                      sel
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
                    }`}
                    disabled={enviando}
                  >
                    <span className="text-2xl font-bold">#{m.numero}</span>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <Users className="h-3 w-3" /> {m.capacidad}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700" role="alert">
              {error}
            </div>
          )}
        </div>

        <footer className="border-t border-slate-200 bg-slate-50 px-5 py-3 flex gap-2">
          <button onClick={onClose} className="btn-ghost flex-1" disabled={enviando}>
            Cancelar
          </button>
          <button
            onClick={handleUnir}
            className="btn-primary flex-1"
            disabled={seleccionadas.size === 0 || enviando}
          >
            {enviando
              ? 'Uniendo...'
              : `Unir ${seleccionadas.size || ''} mesa${seleccionadas.size === 1 ? '' : 's'}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
